import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import {
  fetchBigQueryUsageExecutions,
  fetchBigQueryUsageFeatures,
  fetchBigQueryUsageFingerprints,
  fetchBigQueryUsageMonitoring,
  fetchBigQueryUsageStats,
  fetchBigQueryUsageTenants,
  fetchBigQueryUsageTimeseries,
  type BigQueryUsageExecutionsFilters,
  type BigQueryUsageExecutionsPage,
  type BigQueryUsageFilters,
} from "@/features/bigquery-usage/api/bigquery-usage.api";

/**
 * BigQuery telemetry is written on every query but read by an operator at human
 * pace, so a rolling window goes stale quickly without needing to be live.
 */
export const BIGQUERY_USAGE_STALE_TIME_MS = 30 * 1000;
/**
 * The feature and tenant lists populate filter controls. Both only grow — a new
 * feature when code ships, a new tenant on its first query — so they are read
 * far more lazily than the numbers they narrow.
 */
export const BIGQUERY_USAGE_OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;
/** Cloud Monitoring ingests on its own delay and bills per read. */
export const BIGQUERY_USAGE_MONITORING_STALE_TIME_MS = 5 * 60 * 1000;

export const bigQueryUsageKeys = {
  all: ["bigquery-usage"] as const,
  stats: (filters: BigQueryUsageFilters) => ["bigquery-usage", "stats", filters] as const,
  timeseries: (filters: BigQueryUsageFilters) =>
    ["bigquery-usage", "timeseries", filters] as const,
  fingerprints: (filters: BigQueryUsageFilters, limit?: number) =>
    ["bigquery-usage", "fingerprints", filters, limit ?? null] as const,
  executions: (filters: BigQueryUsageExecutionsFilters) =>
    ["bigquery-usage", "executions", filters] as const,
  features: (windowHours?: number) =>
    ["bigquery-usage", "features", windowHours ?? null] as const,
  tenants: (windowHours?: number) => ["bigquery-usage", "tenants", windowHours ?? null] as const,
  monitoring: (windowHours?: number) =>
    ["bigquery-usage", "monitoring", windowHours ?? null] as const,
} as const;

/** Whether a deferred read has been released by the query that leads it. */
interface DeferrableOptions {
  enabled?: boolean;
}

/**
 * `keepPreviousData` holds the last window's numbers while the next one is in
 * flight, so changing the range moves the strip's values rather than tearing it
 * down to skeletons. `isPlaceholderData` is what says they belong to the old
 * window.
 */
export function bigQueryUsageStatsQueryOptions(filters: BigQueryUsageFilters) {
  return queryOptions({
    queryKey: bigQueryUsageKeys.stats(filters),
    queryFn: () => fetchBigQueryUsageStats(filters),
    staleTime: BIGQUERY_USAGE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
}

export function bigQueryUsageTimeseriesQueryOptions(filters: BigQueryUsageFilters) {
  return queryOptions({
    queryKey: bigQueryUsageKeys.timeseries(filters),
    queryFn: () => fetchBigQueryUsageTimeseries(filters),
    staleTime: BIGQUERY_USAGE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
}

export function bigQueryUsageFingerprintsQueryOptions(
  filters: BigQueryUsageFilters,
  options: DeferrableOptions & { limit?: number } = {},
) {
  return queryOptions({
    queryKey: bigQueryUsageKeys.fingerprints(filters, options.limit),
    queryFn: () => fetchBigQueryUsageFingerprints(filters, options.limit),
    staleTime: BIGQUERY_USAGE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function bigQueryUsageFeaturesQueryOptions(
  windowHours?: number,
  options: DeferrableOptions = {},
) {
  return queryOptions({
    queryKey: bigQueryUsageKeys.features(windowHours),
    queryFn: () => fetchBigQueryUsageFeatures(windowHours),
    staleTime: BIGQUERY_USAGE_OPTIONS_STALE_TIME_MS,
    enabled: options.enabled ?? true,
  });
}

export function bigQueryUsageTenantsQueryOptions(
  windowHours?: number,
  options: DeferrableOptions = {},
) {
  return queryOptions({
    queryKey: bigQueryUsageKeys.tenants(windowHours),
    queryFn: () => fetchBigQueryUsageTenants(windowHours),
    staleTime: BIGQUERY_USAGE_OPTIONS_STALE_TIME_MS,
    enabled: options.enabled ?? true,
  });
}

/**
 * The reconciliation panel. `skipGlobalError` because the route already reports
 * an unreadable Cloud Monitoring as `available: false` rather than an error, so
 * a genuine transport failure here should not raise a global toast over a
 * secondary panel.
 */
export function bigQueryUsageMonitoringQueryOptions(
  windowHours?: number,
  options: DeferrableOptions = {},
) {
  return queryOptions({
    queryKey: bigQueryUsageKeys.monitoring(windowHours),
    queryFn: () => fetchBigQueryUsageMonitoring(windowHours),
    staleTime: BIGQUERY_USAGE_MONITORING_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
    meta: { skipGlobalError: true },
  });
}

export function bigQueryUsageExecutionsInfiniteQueryOptions(
  filters: BigQueryUsageExecutionsFilters,
  options: DeferrableOptions = {},
) {
  return infiniteQueryOptions({
    queryKey: bigQueryUsageKeys.executions(filters),
    queryFn: ({ pageParam }) => fetchBigQueryUsageExecutions(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: BigQueryUsageExecutionsPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export interface BigQueryUsageQueryInput {
  filters: BigQueryUsageFilters;
  /** The executions list's own sort and page size, on top of the shared filter. */
  executions?: Omit<BigQueryUsageExecutionsFilters, keyof BigQueryUsageFilters>;
  /** How many statements the fingerprint table asks for. Capped at 100 by the API. */
  fingerprintLimit?: number;
}

/**
 * Every read the tab needs, fanned out in two waves.
 *
 * Firing all seven routes on mount would be wrong even though each is one HTTP
 * call: every one of them expands into several statements inside the repository,
 * against the API's shared twenty-connection pool. So the KPI strip and the
 * chart lead, and the breakdowns, filter options, reconciliation panel and
 * executions list are held until the lead read has produced a result.
 *
 * The gate is the lead query having resolved either way — an error resolves it
 * too, because a tab that cannot load its headline should still be able to say
 * what it could load. On a window change the lead query holds its previous data,
 * so the second wave is not re-gated and moves with it.
 */
export function useBigQueryUsageQueries(input: BigQueryUsageQueryInput) {
  const { filters, executions, fingerprintLimit } = input;
  const windowHours = filters.windowHours;

  const stats = useQuery(bigQueryUsageStatsQueryOptions(filters));
  const timeseries = useQuery(bigQueryUsageTimeseriesQueryOptions(filters));

  const enabled = !stats.isPending;

  const fingerprints = useQuery(
    bigQueryUsageFingerprintsQueryOptions(filters, { enabled, limit: fingerprintLimit }),
  );
  const features = useQuery(bigQueryUsageFeaturesQueryOptions(windowHours, { enabled }));
  const tenants = useQuery(bigQueryUsageTenantsQueryOptions(windowHours, { enabled }));
  const monitoring = useQuery(bigQueryUsageMonitoringQueryOptions(windowHours, { enabled }));
  const executionsQuery = useInfiniteQuery(
    bigQueryUsageExecutionsInfiniteQueryOptions({ ...filters, ...executions }, { enabled }),
  );

  return {
    stats,
    timeseries,
    fingerprints,
    features,
    tenants,
    monitoring,
    executions: executionsQuery,
    /** False while the second wave is still held back, for a caller that cares. */
    isDeferredReleased: enabled,
  };
}
